export type Facing = -1 | 1;

export type EntityKind = "player" | "enemy" | "boss";

export type HitStrength = "light" | "heavy" | "finisher";

export type ActionName =
  | "idle"
  | "walk"
  | "attack1"
  | "attack2"
  | "attack3"
  | "attack4"
  | "backAttack"
  | "jump"
  | "jumpAttack"
  | "groundAttack"
  | "throw"
  | "propThrow"
  | "propEat"
  | "special"
  | "dash"
  | "fall"
  | "hurt"
  | "down"
  | "getUp"
  | "defeated";

export interface Box {
  x: number;
  y: number;
  width: number;
  depth: number;
}

export interface MoveDefinition {
  duration: number;
  activeStart: number;
  activeEnd: number;
  damage: number;
  reach: number;
  depth: number;
  knockdown?: boolean;
  invulnerable?: boolean;
  hitStop?: number;
  launchDelay?: number;
  launchSpeed?: number;
  lungeSpeed?: number;
}

export const DASH_STYLES = ["hollowKnight", "genshin"] as const;

export interface DashDefinition {
  /** Look of the dash effects. Defaults to hollowKnight. */
  style?: (typeof DASH_STYLES)[number];
  speed: number;
  duration: number;
  /** Seconds of invulnerability from the start of the dash. */
  invulnerable?: number;
  /** Seconds after a dash ends before the next one can start. */
  cooldown: number;
  /** Dashes a full stamina wheel holds. Each dash spends one. */
  charges: number;
  /** Seconds to refill one charge. Refilling pauses while dashing. */
  recharge: number;
}

export interface SpriteAnimationDefinition {
  start: number;
  end: number;
  frameRate: number;
  repeat?: number;
}

export type PlayerEntranceType = "drop" | "rush" | "burst" | "bounce" | "shrinkIn";
export type EnemyEntranceType = "walkIn" | "doorway" | "wallBurst" | "climb" | "vehicle";
export type EntranceType = PlayerEntranceType | EnemyEntranceType;

export const SPAWN_SPOT_STYLES = [
  "open",
  "tunnel",
  "rollup",
  "slide",
  "holo",
  "screen",
  "hatch",
  "wall",
  "glass",
  "shelf",
  "manhole",
  "cybertruck"
] as const;

/** How a painted stage feature lets an enemy through. */
export type SpawnSpotStyle = (typeof SPAWN_SPOT_STYLES)[number];

/** A door, tunnel, hatch, or curb painted into the stage art that enemies arrive through. */
export interface LevelSpawnSpot {
  id: string;
  style: SpawnSpotStyle;
  /** World x at the middle of the opening. */
  x: number;
  /** World y of the opening's sill, where an arriving enemy's feet start. For a truck, its lane. */
  y: number;
  width: number;
  height: number;
  /** Floor an enemy drops or steps down to when the sill sits above the walkable floor. */
  floorY?: number;
  /** Scale an enemy starts at deep inside an opening that recedes, such as a tunnel or a far door. */
  depthScale?: number;
  /** Top and bottom y of the painted shutter or doors that open before anyone comes through. */
  panel?: [number, number];
  /** Side a truck drives in from. */
  direction?: Facing;
}
export type StageBackdrop =
  | "storefront"
  | "street"
  | "factory"
  | "tunnel"
  | "hangar"
  | "corner"
  | "horizon";

export interface EntranceDefinition {
  type: PlayerEntranceType;
  label: string;
  duration: number;
}

export interface EnemyEntranceDefinition {
  type: EnemyEntranceType;
  direction?: Facing;
  duration?: number;
  delay?: number;
  /** Spawn spot ids in the level to arrive through. Enemies spread across them, least used first. */
  from?: string[];
}

export interface CharacterDefinition {
  id: string;
  displayName: string;
  role: string;
  palette: {
    primary: string;
    secondary: string;
    accent: string;
  };
  stats: {
    walkSpeed: number;
    jumpSpeed: number;
    maxHealth: number;
    maxEnergy: number;
  };
  special: {
    type: "beam" | "burst" | "rush" | "strike" | "canvas";
    cost: number;
    label: string;
    shout?: string;
    enabled?: boolean;
  };
  entrance?: EntranceDefinition;
  /** Double-tap left or right to dash. Fighters without it cannot dash. */
  dash?: DashDefinition;
  hurtbox: Box;
  moves: Record<
    | "attack1"
    | "attack2"
    | "attack3"
    | "backAttack"
    | "jumpAttack"
    | "groundAttack"
    | "throw"
    | "special",
    MoveDefinition
  > &
    Partial<Record<"attack4", MoveDefinition>>;
  spriteSheet?: {
    image: string;
    frameWidth: number;
    frameHeight: number;
    scale?: number;
    /** Square of the idle frame, in frame pixels, that frames the head for HUD and results portraits. */
    portrait?: { x: number; y: number; size: number };
    animations: Partial<Record<ActionName, SpriteAnimationDefinition>>;
  };
}

export interface EnemyDefinition {
  id: string;
  displayName: string;
  archetype: "rusher" | "gunner" | "bruiser" | "boss";
  palette: {
    primary: string;
    secondary: string;
    accent: string;
  };
  maxHealth: number;
  walkSpeed: number;
  attackRange: number;
  attackDamage: number;
  attackDuration: number;
  score: number;
  hurtbox: Box;
  spriteSheet?: CharacterDefinition["spriteSheet"];
  entrance?: EnemyEntranceDefinition;
}

export interface WaveSpawn {
  enemyId: string;
  baseCount: number;
  extraPerPlayer: number;
  entrance?: EnemyEntranceDefinition;
}

export interface LevelWave {
  id: string;
  triggerX: number;
  lockX: number;
  banner: string;
  spawns: WaveSpawn[];
}

export type PropKind = "splitKeyboard" | "yogaBall" | "coffeeBag";

export type PropPhase = "ground" | "spinning" | "flying" | "eating" | "bouncing";

export interface LevelProp {
  kind: PropKind;
  x: number;
  y: number;
}

export interface LevelDefinition {
  id: string;
  name: string;
  width: number;
  groundTop: number;
  groundBottom: number;
  playerStart: { x: number; y: number };
  waves: LevelWave[];
  props?: LevelProp[];
  spawnSpots?: LevelSpawnSpot[];
  /** Screen y of the front edge that climbing enemies pull themselves over. */
  ledgeY?: number;
}

export interface PropState {
  id: string;
  kind: PropKind;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  facing: Facing;
  phase: PropPhase;
  ownerId?: string;
  targetId?: string;
  hitIds: string[];
  flightTime: number;
  bouncesRemaining?: number;
}

export const DIFFICULTY_TIERS = ["easy", "medium", "hard", "terminal"] as const;

export type DifficultyTier = (typeof DIFFICULTY_TIERS)[number];

export interface DifficultyTierProfile {
  enemyHealth: number;
  bossHealth: number;
  damageBonus: number;
  speed: number;
  cooldown: number;
  coffeeDuration: number;
  coffeeRegen: number;
  coffeeBags: number;
  /** Stocks including the one you are on. One means the first death is final. */
  lives: number;
  /** Share of a non-boss's scaled max health dealt by a player super. */
  specialFraction: number;
  /** Authored max health at or below this is executed by a super. Zero disables it. */
  specialExecuteHealth: number;
}

export interface DifficultyDefinition {
  maxWaveEnemies: number;
  soloEnemyHealth: number;
  enemyHealthPerExtraPlayer: number;
  soloBossHealth: number;
  bossHealthPerExtraPlayer: number;
  largePartyBossHealthPerExtraPlayer: number;
  enemyDamageBonusAtPlayers: number;
  enemyDamageBonus: number;
  tiers: Record<DifficultyTier, DifficultyTierProfile>;
}

export interface TuningDefinition {
  fixedStep: number;
  gravity: number;
  maxFallSpeed: number;
  walkAcceleration: number;
  walkDeceleration: number;
  enemyAcceleration: number;
  attackLungeSpeed: number;
  /** Both taps of a dash double-tap must land within this many seconds. */
  dashTapWindow: number;
  throwRange: number;
  depthTolerance: number;
  comboBuffer: number;
  comboReset: number;
  playerHurtTime: number;
  enemyHurtTime: number;
  knockdownTime: number;
  getUpDuration: number;
  quickRiseDelay: number;
  getUpInvulnerability: number;
  hurtInvulnerability: number;
  separationRadius: number;
  separationDepth: number;
  cameraLead: number;
  stallWarningSeconds: number;
  stallDamageSeconds: number;
  maxPlayers: number;
  difficulty: DifficultyDefinition;
}

export interface GameContent {
  characters: CharacterDefinition[];
  enemies: EnemyDefinition[];
  levels: LevelDefinition[];
  tuning: TuningDefinition;
}

export interface InputFrame {
  x: -1 | 0 | 1;
  y: -1 | 0 | 1;
  attack: boolean;
  jump: boolean;
  special: boolean;
}

export const EMPTY_INPUT: InputFrame = {
  x: 0,
  y: 0,
  attack: false,
  jump: false,
  special: false
};

export interface EntityState {
  id: string;
  kind: EntityKind;
  definitionId: string;
  displayName: string;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  facing: Facing;
  health: number;
  maxHealth: number;
  energy: number;
  maxEnergy: number;
  lives: number;
  specialOrbs: number;
  score: number;
  kills: number;
  deaths: number;
  continues: number;
  action: ActionName;
  actionTime: number;
  comboStep: number;
  comboTimer: number;
  attackConnected: string[];
  hurtTimer: number;
  downTimer: number;
  invulnerableTimer: number;
  entranceTimer?: number;
  entranceType?: EntranceType;
  entranceDuration?: number;
  entranceDirection?: Facing;
  entranceBackdrop?: StageBackdrop;
  /** Spawn spot id an enemy is arriving through. */
  entranceSpot?: string;
  /** Order an enemy leaves its spawn spot in, which picks its truck door. */
  entranceQueue?: number;
  targetId?: string;
  ownerId?: string;
  aiCooldown: number;
  spawnOrder: number;
  nameColor?: string;
  heldPropId?: string;
  buffTimer?: number;
  bossPhase?: 1 | 2;
  bossTransitionTimer?: number;
  specialVariant?: "flame" | "maxQ" | "smokingMeats" | "nova";
  specialAnchorX?: number;
  artTimer?: number;
  novaCooldown?: number;
  dashCooldown?: number;
  /** Dash charges on hand, fractional while one refills. */
  stamina?: number;
  airDashUsed?: boolean;
  /** Open manhole a player has fallen down, and where they climb back out. */
  fall?: { spot: string; fromX: number; fromY: number; toX: number; toY: number };
  forcedMotion?: {
    type: "cableThrow" | "orcaThrash" | "tickle";
    elapsed: number;
    duration: number;
    fromX: number;
    fromY: number;
    toX: number;
    toY: number;
    apex: number;
    direction: Facing;
  };
}

export interface WorldEvent {
  id: number;
  type:
    | "hit"
    | "knockdown"
    | "special"
    | "dash"
    | "dashDenied"
    | "manholeFall"
    | "cable"
    | "cableThrow"
    | "slam"
    | "flame"
    | "wave"
    | "boss"
    | "darioTransform"
    | "darioBolt"
    | "maxQ"
    | "smokingMeats"
    | "bossNova"
    | "defeated"
    | "levelComplete"
    | "warning"
    | "entrance"
    | "propGrab"
    | "propThrow"
    | "propEat"
    | "propHit";
  x: number;
  y: number;
  z?: number;
  text?: string;
  direction?: Facing;
  strength?: HitStrength;
  entityId?: string;
  targetId?: string;
  propKind?: PropKind;
  characterId?: string;
  entranceType?: EntranceType;
  duration?: number;
  delay?: number;
  backdrop?: StageBackdrop;
  spot?: string;
  queue?: number;
}

export interface WorldState {
  version: 1;
  tick: number;
  elapsed: number;
  cameraX: number;
  hitStopTimer: number;
  levelIndex: number;
  levelTransitionTimer: number;
  waveIndex: number;
  activeWaveId: string | null;
  banner: string | null;
  bannerTimer: number;
  stallTimer: number;
  levelComplete: boolean;
  exitOpen: boolean;
  spawnCounter: number;
  eventCounter: number;
  players: Record<string, EntityState>;
  enemies: Record<string, EntityState>;
  props: Record<string, PropState>;
  events: WorldEvent[];
  difficultyTier?: DifficultyTier;
  /** Elapsed time each manhole's cover comes off. It stays open until the level changes. */
  openManholes?: Record<string, number>;
}

export interface LobbyPlayer {
  id: string;
  slot: number;
  characterId: string;
  displayName: string;
  color: string;
  ready: boolean;
  inMatch: boolean;
}

export interface LobbyState {
  roomCode: string;
  players: LobbyPlayer[];
  difficulty: DifficultyTier;
  hostId: string;
}

export type RoomPhase = "lobby" | "playing";

export type ClientMessage =
  | { type: "input"; seq: number; input: InputFrame }
  | { type: "continue" }
  | { type: "swap"; characterId: string }
  | { type: "ready"; ready: boolean }
  | { type: "rename"; displayName: string | null }
  | { type: "return" }
  | { type: "difficulty"; tier: DifficultyTier };

export type ServerMessage =
  | {
      type: "welcome";
      playerId: string;
      roomCode: string;
      phase: RoomPhase;
      lobby?: LobbyState;
      state?: WorldState;
    }
  | { type: "lobby"; lobby: LobbyState; phase: RoomPhase }
  | { type: "start"; state: WorldState }
  | { type: "snapshot"; serverTime: number; ack: number; state: WorldState }
  | { type: "error"; message: string };
